// src/encounter/services/use-lab-catalogue.ts
// The lab catalogue v2 read for the Add lab popup (PROD-2640, API from PROD-2637).
//
// HOW V2 AVAILABILITY IS DETECTED: there is no client-visible flag. The popup asks the v2 API for a
// PUBLISHED panel with the picked lab's code (and system), and uses v2 only when both calls succeed
// and one matches. Any failure means "not available" and resolves to `null`, never an error, so the
// dialog falls back to today's LabOrderValueSet flow untouched:
//   - 503 while the v2 tables are not deployed in an environment (dev today: scripts not run);
//   - 404 / 401 / network errors;
//   - an empty list, or no published panel with that code/system.
// The Master flag `Features:LabCatalogueV2Reads` only switches the legacy LabOrderValueSet* endpoints
// onto the v2 tables; the /LabCatalogue endpoints themselves are not behind it.
//
//   useLabCataloguePanel({ code, system })  → lookup by the picked lab (a new order)
//   useLabCataloguePanel({ panelId })       → the exact version a saved order was placed on (edit)
import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import {
  pickPublishedPanel,
  type LabCataloguePanelDto,
  type LabCataloguePanelListDto,
} from '../lib/lab-catalogue';

const BASE = '/api/Master/LabCatalogue/panels';

export interface LabCatalogueLookup {
  code?: string | null;
  system?: string | null;
  /** A saved order's panelDefinitionId: load that version directly. Wins over code/system. */
  panelId?: string | null;
  /** false = do not look anything up (the hook resolves to nothing). */
  enabled?: boolean;
}

async function loadDetail(id: string, signal: AbortSignal): Promise<LabCataloguePanelDto | null> {
  const detail = await get<LabCataloguePanelDto>(`${BASE}/${encodeURIComponent(id)}`, { signal });
  return detail?.id ? detail : null;
}

export function useLabCataloguePanel({ code, system, panelId, enabled = true }: LabCatalogueLookup) {
  const c = code?.trim() ?? '';
  const s = system?.trim() ?? '';
  const id = panelId?.trim() ?? '';
  return useQuery<LabCataloguePanelDto | null>({
    queryKey: ['lab-catalogue-panel', id || null, id ? null : c, id ? null : s || null],
    enabled: enabled && (!!id || c.length > 0),
    retry: false,
    staleTime: 5 * 60_000,
    queryFn: async ({ signal }) => {
      try {
        if (id) return await loadDetail(id, signal);
        const params = new URLSearchParams({ status: 'Published', search: c, take: '50' });
        const list = await get<LabCataloguePanelListDto>(`${BASE}?${params.toString()}`, { signal });
        const hit = pickPublishedPanel(list?.items, c, s);
        if (!hit) return null;
        const detail = await loadDetail(hit.id, signal);
        return detail && (detail.status ?? '').toLowerCase() === 'published' ? detail : null;
      } catch {
        // DELIBERATE, not a swallowed bug: do not surface this error. LabCatalogueController's
        // `Guarded` answers 503 while the v2 tables are not deployed in an environment (dev until
        // db/lab-catalogue/01-03 run), on the list AND the detail call. That, a 404 / 401, or a
        // network error all mean "v2 not available here", and `null` is the dialog's cue to use the
        // LabOrderValueSet flow. Throwing would put the popup in an error state instead.
        return null;
      }
    },
  });
}
